/**
 * Property list — `GET properties/`, scoped to the caller by the backend
 * (buildScope: a landlord sees their own, an agent sees owned+assigned, a
 * superagent sees all). Status filter + free-text search are server-side.
 *
 * A SUPERAGENT gets approve/reject on `PENDING_APPROVAL` rows — the only
 * role that can move a property's status (properties/routes.ts:800-822).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Column, Grid, InlineLoading, Select, SelectItem, TextInput } from '@carbon/react';
import { Add, Edit } from '@carbon/icons-react';
import { Building2 } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { parseApiError } from '../../lib/apiError';
import { PropertyPicture } from '../../components/PropertyPicture';
import { EmptyState } from '../../components/EmptyState';
import { Notify } from '../../components/carbon/Notify';
import { Pager } from '../../components/carbon/Pager';
import { PageHeader } from '../../components/carbon/PageHeader';
import { StatusTag } from '../../components/carbon/StatusTag';
import {
  ResponsiveDataTable,
  type Column as DataColumn,
} from '../../components/carbon/ResponsiveDataTable';
import { approveProperty, listProperties, rejectProperty } from '../../api/endpoints/properties';
import { PROPERTY_STATUSES, type PropertyListRow, type PropertyStatus } from '../../types/property';

export function PropertyListPage(): React.JSX.Element {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<PropertyStatus | ''>('');
  const [search, setSearch] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['properties', { page, status, search }],
    queryFn: () =>
      listProperties({
        page,
        status: status === '' ? undefined : status,
        search: search === '' ? undefined : search,
      }),
  });

  const approve = useMutation({
    mutationFn: approveProperty,
    onSuccess: () => {
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
    onError: (caught) => setActionError(parseApiError(caught).message),
  });

  const reject = useMutation({
    mutationFn: rejectProperty,
    onSuccess: () => {
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
    onError: (caught) => setActionError(parseApiError(caught).message),
  });

  const properties = query.data?.results ?? [];
  const count = query.data?.count ?? 0;
  const busy = approve.isPending || reject.isPending;

  const columns: readonly DataColumn<PropertyListRow>[] = [
    {
      key: 'photo',
      header: 'Photo',
      render: (row) =>
        row.primary_image?.image != null ? (
          <PropertyPicture className="thumb" src={row.primary_image.image} alt="" size="thumb" />
        ) : (
          <span className="thumb thumb--placeholder" aria-hidden="true">
            —
          </span>
        ),
    },
    { key: 'name', header: 'Name', render: (row) => <Link to={`${row.id}`}>{row.name}</Link> },
    { key: 'type', header: 'Type', render: (row) => row.property_type },
    { key: 'address', header: 'Address', render: (row) => `${row.address}, ${row.city}` },
    { key: 'status', header: 'Status', render: (row) => <StatusTag status={row.status} /> },
    {
      key: 'units',
      header: 'Units',
      render: (row) => `${row.vacant_units_count} / ${row.total_units} vacant`,
    },
    { key: 'owner', header: 'Owner', render: (row) => row.owner_name },
    { key: 'agent', header: 'Agent', render: (row) => row.agent_name ?? '—' },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <span className="row-actions">
          <Button kind="ghost" size="sm" as={Link} to={`${row.id}/edit`} renderIcon={Edit} iconDescription="Edit">
            Edit
          </Button>
          {user?.role === 'SUPERAGENT' && row.status === 'PENDING_APPROVAL' && (
            <>
              <Button kind="ghost" size="sm" disabled={busy} onClick={() => approve.mutate(row.id)}>
                Approve
              </Button>
              <Button kind="ghost" size="sm" disabled={busy} onClick={() => reject.mutate(row.id)}>
                Reject
              </Button>
            </>
          )}
        </span>
      ),
    },
  ];

  return (
    <section>
      <PageHeader
        title="Properties"
        actions={
          <Button as={Link} to="new" renderIcon={Add}>
            Add property
          </Button>
        }
      />

      {actionError !== null && <Notify kind="error" title={actionError} className="page-notify" />}

      <Grid
        className="filter-grid"
        as="form"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
        }}
      >
        <Column sm={4} md={4} lg={6}>
          <TextInput
            id="property-search"
            labelText="Search"
            placeholder="Name, address, city"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Column>
        <Column sm={4} md={3} lg={4}>
          <Select
            id="property-status"
            labelText="Status"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as PropertyStatus | '');
              setPage(1);
            }}
          >
            <SelectItem value="" text="All" />
            {PROPERTY_STATUSES.map((value) => (
              <SelectItem key={value} value={value} text={value} />
            ))}
          </Select>
        </Column>
        <Column sm={4} md={1} lg={2} className="filter-grid__submit">
          <Button type="submit" kind="secondary">
            Apply
          </Button>
        </Column>
      </Grid>

      {query.isPending && <InlineLoading description="Loading properties…" />}
      {query.isError && <Notify kind="error" title={parseApiError(query.error).message} />}

      {query.isSuccess && properties.length === 0 && (
        <EmptyState icon={Building2} message="No properties found." action={{ label: 'Add property', to: 'new' }} />
      )}

      {query.isSuccess && properties.length > 0 && (
        <>
          <ResponsiveDataTable
            caption="Properties"
            columns={columns}
            rows={properties}
            rowId={(row) => String(row.id)}
          />
          <Pager page={page} count={count} onPageChange={setPage} />
        </>
      )}
    </section>
  );
}

export default PropertyListPage;
